import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { 
  FaChevronDown, 
  FaShieldAlt, 
  FaUserTie, 
  FaBullhorn, 
  FaHeart, 
  FaBalanceScale,
  FaServer,
  FaCloud,
  FaCode,
  FaCogs
} from 'react-icons/fa';
import Button from '../shared/Button';
import logo from '../../assets/images/vistahand-logo.png';
import './Header.css';

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMenuOpen(prev => !prev);
  };

  const toggleServicesMobile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsServicesOpen(prev => !prev);
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      {isMenuOpen && <div className="nav-overlay" onClick={closeMenu}></div>}

      <div className="navbar-pill">
        <div className="navbar-left">
          <Link to="/" className="nav-logo" onClick={closeMenu}>
            <img src={logo} alt="VistaHand Logo" />
          </Link>
        </div>

        <nav className={`nav-links ${isMenuOpen ? 'mobile-active' : ''}`}>
          <NavLink to="/" onClick={closeMenu}>Home</NavLink>
          
          <div className={`nav-dropdown-item ${isServicesOpen ? 'active' : ''}`}>
            <div className="dropdown-trigger-wrapper">
              <NavLink to="/services" onClick={closeMenu} className="dropdown-trigger-link">
                Services
              </NavLink>
              <button 
                type="button"
                className="dropdown-chevron-btn" 
                onClick={toggleServicesMobile}
                aria-label="Toggle Services Menu"
              >
                <FaChevronDown className={`chevron-icon ${isServicesOpen ? 'rotate' : ''}`} size={12} />
              </button>
            </div>

            <div className="dropdown-mega-menu">
              <div className="mega-menu-container">
                <div className="mega-column">
                  <h4>Managed IT & Cloud (MSP)</h4>
                  <Link to="/services/managed-it" className="mega-item" onClick={closeMenu}>
                    <div className="mega-icon"><FaServer /></div>
                    <div className="mega-text">
                      <span>Managed IT</span>
                      <small>15-min SLA & RMM</small>
                    </div>
                  </Link>
                  <Link to="/services/cloud-infrastructure" className="mega-item" onClick={closeMenu}>
                    <div className="mega-icon"><FaCloud /></div>
                    <div className="mega-text">
                      <span>Cloud & DevOps</span>
                      <small>Azure, GCP, Hybrid Cloud</small>
                    </div>
                  </Link>
                  <Link to="/services/cybersecurity" className="mega-item" onClick={closeMenu}>
                    <div className="mega-icon"><FaShieldAlt /></div>
                    <div className="mega-text">
                      <span>Cybersecurity</span>
                      <small>24/7 SOC & MDR</small>
                    </div>
                  </Link>
                  <Link to="/services/web-development" className="mega-item" onClick={closeMenu}>
                    <div className="mega-icon"><FaCode /></div>
                    <div className="mega-text">
                      <span>Engineering</span>
                      <small>Custom Web & Mobile</small>
                    </div>
                  </Link>
                  <Link to="/services/automation" className="mega-item" onClick={closeMenu}>
                    <div className="mega-icon"><FaCogs /></div>
                    <div className="mega-text">
                      <span>AI & Automation</span>
                      <small>RPA & Workflow Ops</small>
                    </div>
                  </Link>
                </div>

                <div className="mega-column">
                  <h4>Managed Workforce & BPO</h4>
                  <Link to="/services/remote-staffing" className="mega-item" onClick={closeMenu}>
                    <div className="mega-icon"><FaUserTie /></div>
                    <div className="mega-text">
                      <span>Virtual Talent</span>
                      <small>Dedicated teams</small>
                    </div>
                  </Link>
                  <Link to="/services/medical-process-outsourcing" className="mega-item" onClick={closeMenu}>
                    <div className="mega-icon"><FaHeart /></div>
                    <div className="mega-text">
                      <span>Medical BPO</span>
                      <small>HIPAA compliant</small>
                    </div>
                  </Link>
                  <Link to="/services/legal-process-outsourcing" className="mega-item" onClick={closeMenu}>
                    <div className="mega-icon"><FaBalanceScale /></div>
                    <div className="mega-text">
                      <span>Legal BPO</span>
                      <small>Precision workflow</small>
                    </div>
                  </Link>
                  <Link to="/services/digital-marketing" className="mega-item" onClick={closeMenu}>
                    <div className="mega-icon"><FaBullhorn /></div>
                    <div className="mega-text">
                      <span>Digital Growth</span>
                      <small>Strategy & Ops</small>
                    </div>
                  </Link>
                </div>

                <div className="mega-column promo-column">
                  <div className="mega-promo-card">
                    <h5>Enterprise MSP SLAs</h5>
                    <p>⚡ <strong>15-Min Response</strong> SLA Guarantee<br/>🎧 <strong>24/7/365</strong> Live Helpdesk<br/>🛡️ <strong>SOC2 & HIPAA</strong> Certified</p>
                    <Link to="/services/managed-it" className="mega-cta" onClick={closeMenu}>Get MSP Proposal →</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <NavLink to="/about" onClick={closeMenu}>About</NavLink>
          <NavLink to="/portfolio" onClick={closeMenu}>Portfolio</NavLink>
          <NavLink to="/testimonials" onClick={closeMenu}>Testimonials</NavLink>
          
          <div className="mobile-cta-section">
            <Button href="/contact" variant="primary" onClick={closeMenu} className="mobile-cta-btn">
              Get MSP Proposal
            </Button>
          </div>
        </nav>

        <div className="navbar-right">
          <Button href="/contact" variant="white" onClick={closeMenu} className="header-button">
            Contact Us
          </Button>

          <button 
            className={`mobile-toggle ${isMenuOpen ? 'active' : ''}`} 
            onClick={toggleMobileMenu}
            aria-label="Toggle Mobile Menu"
            type="button"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
    </header>
  );
}

export default Header;
